"use client";

import { CSSProperties, ReactNode } from "react";
import { QuoteThemeFontLoader } from "./quote-theme-font-loader";
import { getQuoteThemeClassNames, DUAL_TONE_STYLE_A_HEADING_CSS } from "./quote-theme-class-maps";
import type { ResolvedQuoteTheme } from "./quote-theme-types";

const DUAL_FONT_HEADING_CSS = `
  .quote-theme-root[data-font-schema="DUAL_FONT"] :is(h2, h3):not(.text-xs):not(.text-sm):not(.quote-itinerary-item-title):not(.quote-custom-template-block *):not(.quote-footer *) {
    font-family: var(--quote-font-heading);
  }
  .quote-theme-root[data-font-schema="DUAL_FONT"] .quote-itinerary-item-title {
    font-family: var(--quote-font-body);
  }
`;

export function QuoteThemeRoot({
  theme,
  children,
}: {
  theme: ResolvedQuoteTheme;
  children: ReactNode;
}) {
  const classNames = getQuoteThemeClassNames(
    theme.uiStyle,
    theme.config.colorSchema
  );

  const style = {
    "--quote-color-primary": theme.tokens.primary,
    "--quote-color-secondary": theme.tokens.secondary,
    "--quote-color-primary-10": theme.tokens.primary10,
    "--quote-color-primary-20": theme.tokens.primary20,
    "--quote-color-on-primary": theme.tokens.onPrimary,
    "--quote-color-block-heading": theme.tokens.blockHeadingColor,
    "--quote-font-heading": theme.tokens.headingFont,
    "--quote-font-body": theme.tokens.bodyFont,
    "--quote-radius-card": theme.tokens.radiusCard,
    "--quote-radius-pill": theme.tokens.radiusPill,
    "--quote-shadow-card": theme.tokens.shadowCard,
    "--quote-section-gap": theme.tokens.sectionGap,
    fontFamily: theme.tokens.bodyFont,
  } as CSSProperties;

  const showDualToneStyleAHeadings =
    theme.config.colorSchema === "DUAL_TONE" && theme.uiStyle === "STYLE_A";

  return (
    <>
      <QuoteThemeFontLoader googleFontsUrl={theme.googleFontsUrl} />
      {theme.config.fontSchema === "DUAL_FONT" ? (
        <style>{DUAL_FONT_HEADING_CSS}</style>
      ) : null}
      {showDualToneStyleAHeadings ? (
        <style>{DUAL_TONE_STYLE_A_HEADING_CSS}</style>
      ) : null}
      <div
        className={`quote-theme-root w-full ${classNames.pageBackground}`}
        data-ui-style={theme.uiStyle}
        data-layout={theme.layoutConfig}
        data-font-schema={theme.config.fontSchema}
        data-color-schema={theme.config.colorSchema}
        style={style}
      >
        {children}
      </div>
    </>
  );
}
